import React from 'react';
import { v4 } from 'uuid';

import { NewLayoutPlacement } from '@neptune/shared/grid-layout-domain';
import { Widget, WidgetDefinitionNew, WidgetType } from '@neptune/shared/widgets-domain';

import { useAddWidgetModal } from './use-add-widget-modal';

type UseAddWidgetParams = {
  widgetDefinitions: WidgetDefinitionNew[];
  newWidgetPlacement?: NewLayoutPlacement;
  onAddWidgets?: (widget: Widget[], placement: NewLayoutPlacement) => void;
};

function createWidgetFromTemplate(template: Omit<Widget, 'id'>): Widget {
  return {
    ...template,
    id: v4(),
  };
}

export const useAddWidget = ({
  widgetDefinitions,
  newWidgetPlacement,
  onAddWidgets,
}: UseAddWidgetParams) => {
  const { openWithData } = useAddWidgetModal();

  return React.useCallback(
    (widgetType: WidgetType, returnFocusTargetId?: string) => {
      const definition = widgetDefinitions.find((def) => def.widgetType === widgetType);

      if (!definition) {
        throw new Error(`Widget type ${widgetType} is not defined in the widget repository`);
      }

      if (definition.hasSettings) {
        return openWithData({
          returnFocusTargetId,
          widgetType,
          widgetPlacement: newWidgetPlacement,
        });
      }

      if (definition.template && onAddWidgets) {
        onAddWidgets(
          [createWidgetFromTemplate(definition.template)],
          newWidgetPlacement ?? { at: 'bottom' },
        );
      }
    },
    [newWidgetPlacement, onAddWidgets, openWithData, widgetDefinitions],
  );
};
